import { ReactNode } from "react";
import styles from "./DefaultTemplate.module.scss";
import { clsx } from "clsx";
import { useRouteMainFocus } from "../utils/useRouteMainFocus.ts";
import useAccessiblePageTitle from "../../../common/page-title/useAccessiblePageTitle.tsx";
import BaseHeader, {
  BaseHeaderProps,
} from "../../../common/header/BaseHeader.tsx";

interface DefaultTemplateProps {
  children: ReactNode;
  title: string;
  noInset?: boolean;
  /**
   * Class name for element wrapping the header and main content, but
   * not the panel. A good place for CSS vars that apply to both regions.
   * This region also has a queryable container named `template-content`
   * available.
   */
  contentClassName?: string;
  headerConfig?: BaseHeaderProps;
  // This is useful if you need to separately embed the header inside a fancy banner
  noHeader?: boolean;
}

export const rightContentTargetId = "right-content-target";

/**
 * DefaultTemplate is intended for full-width page content with right side
 * panel support.
 */
export default function DefaultTemplate(props: DefaultTemplateProps) {
  const accessiblePageTitle = useAccessiblePageTitle(props.title);

  useRouteMainFocus();

  return (
    <div className={styles.page} id={rightContentTargetId}>
      <div
        className={clsx(
          styles.innerContentNoPanel,
          props.noInset && styles.noInset
        )}
      >
        <div className={props.contentClassName}>
          {props.noHeader ? null : <BaseHeader {...props?.headerConfig} />}
          <main
            aria-label={accessiblePageTitle}
            className={clsx(styles.primaryContent)}
          >
            {props.children}
          </main>
        </div>
      </div>
      {/* Panel goes here, flexible size/layout/content/etc. Single element only. */}
    </div>
  );
}
